import React from 'react';
import { useLocation } from 'react-router-dom';
import { useCompanySecurityValidator } from '@/hooks/useCompanySecurityValidator';
import { useAuth } from '@/hooks/useAuth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertTriangle } from 'lucide-react';
import { SecurityStatusIndicator } from '@/components/SecurityStatusIndicator';
import { SecurityErrorBoundary } from '@/components/SecurityErrorBoundary';

interface CompanySecurityWrapperProps {
  children: React.ReactNode;
}

// Rutas que no requieren empresa seleccionada
const ROUTES_WITHOUT_COMPANY_REQUIREMENT = [
  '/auth',
  '/reset-password',
  '/companies', // Página para seleccionar/crear empresas
  '/modularis',
  '/admin',
  '/configuration',
  '/site-map' // No requiere empresa
];

/**
 * Wrapper de seguridad que monitorea constantemente el contexto de empresa
 * y previene acceso no autorizado a datos de otras empresas
 */
export const CompanySecurityWrapper: React.FC<CompanySecurityWrapperProps> = ({ 
  children 
}) => {
  return (
    <SecurityErrorBoundary>
      <CompanySecurityWrapperInner>
        {children}
      </CompanySecurityWrapperInner>
    </SecurityErrorBoundary>
  );
};

const CompanySecurityWrapperInner: React.FC<CompanySecurityWrapperProps> = ({ 
  children 
}) => {
  const location = useLocation();
  const { user, selectedCompany, loading } = useAuth();
  const { isValidating, lastValidationTime } = useCompanySecurityValidator();
  
  // Verificar si la ruta actual está en la lista de rutas permitidas sin empresa
  const isRouteAllowedWithoutCompany = ROUTES_WITHOUT_COMPANY_REQUIREMENT.some(route => 
    location.pathname.startsWith(route)
  );

  // No mostrar nada si aún está cargando
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border border-primary border-t-transparent"></div>
    </div>;
  }

  // No mostrar contenido si no hay usuario
  if (!user) {
    return <>{children}</>;
  }

  // Mostrar alerta si no hay empresa seleccionada pero el usuario está autenticado
  // Solo para rutas que requieren empresa seleccionada
  if (user && !selectedCompany && !isRouteAllowedWithoutCompany) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Configurando contexto de empresa...</strong><br/>
              Por favor espera mientras se configura tu acceso seguro a las empresas.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Contenido de la aplicación */}
      {children}

      {/* Indicador de seguridad minimalista (solo en desarrollo) */}
      {process.env.NODE_ENV === 'development' && (
        <SecurityStatusIndicator />
      )}
    </>
  );
};